import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { t } from "@lingui/macro";
import { CaretRight, DotsSixVertical, PlusCircle } from "@phosphor-icons/react";
import {
  Award,
  Certification,
  CustomSection,
  Education,
  Experience,
  Interest,
  Language,
  Profile,
  Project,
  Publication,
  Reference,
  SectionItem,
  SectionKey,
  Skill,
  Volunteer,
} from "@reactive-resume/schema";
import { Button, ScrollArea, Separator, Tooltip } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { Fragment, useMemo } from "react";

import { useResumeStore } from "@/client/stores/resume";

import { FirstRunPrompt } from "./first-run-prompt";
import { OnboardingHints } from "./onboarding-hints";
import { BasicsSection } from "./sections/basics";
import { SectionBase } from "./sections/shared/section-base";
import { SummarySection } from "./sections/summary";

type SectionConfig = {
  id: SectionKey;
  title: (item: SectionItem) => string;
  description?: (item: SectionItem) => string | undefined;
};

const builtInSections: SectionConfig[] = [
  {
    id: "profiles",
    title: (i) => (i as Profile).network,
    description: (i) => (i as Profile).username,
  },
  { id: "summary", title: () => "", description: () => undefined },
  {
    id: "experience",
    title: (i) => (i as Experience).company,
    description: (i) => (i as Experience).position,
  },
  {
    id: "education",
    title: (i) => (i as Education).institution,
    description: (i) => (i as Education).area,
  },
  {
    id: "skills",
    title: (i) => (i as Skill).name,
    description: (i) => {
      const s = i as Skill;
      if (s.description) return s.description;
      if (s.keywords.length > 0) return `${s.keywords.length} keywords`;
      return undefined;
    },
  },
  {
    id: "languages",
    title: (i) => (i as Language).name,
    description: (i) => (i as Language).description,
  },
  { id: "awards", title: (i) => (i as Award).title, description: (i) => (i as Award).awarder },
  {
    id: "certifications",
    title: (i) => (i as Certification).name,
    description: (i) => (i as Certification).issuer,
  },
  {
    id: "interests",
    title: (i) => (i as Interest).name,
    description: (i) => {
      const s = i as Interest;
      if (s.keywords.length > 0) return `${s.keywords.length} keywords`;
      return undefined;
    },
  },
  {
    id: "projects",
    title: (i) => (i as Project).name,
    description: (i) => (i as Project).description,
  },
  {
    id: "publications",
    title: (i) => (i as Publication).name,
    description: (i) => (i as Publication).publisher,
  },
  {
    id: "volunteer",
    title: (i) => (i as Volunteer).organization,
    description: (i) => (i as Volunteer).position,
  },
  {
    id: "references",
    title: (i) => (i as Reference).name,
    description: (i) => (i as Reference).description,
  },
];

const configMap = new Map(builtInSections.map((s) => [s.id, s]));

type SortableSectionProps = {
  sectionId: string;
  columnLabel: string;
  onToggleColumn: () => void;
  children: React.ReactNode;
};

const SortableSection = ({
  sectionId,
  columnLabel,
  onToggleColumn,
  children,
}: SortableSectionProps) => {
  const { setNodeRef, transform, transition, attributes, listeners, isDragging } = useSortable({
    id: sectionId,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
    zIndex: isDragging ? 50 : undefined,
    position: "relative" as const,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div className="flex items-start">
        <div
          {...listeners}
          {...attributes}
          className={cn(
            "text-muted-foreground/50 hover:text-muted-foreground mt-0.5 flex shrink-0 cursor-grab items-center justify-center py-1 pr-1 transition-colors active:cursor-grabbing",
          )}
        >
          <DotsSixVertical weight="bold" size={14} />
        </div>

        <div className="min-w-0 flex-1">{children}</div>
      </div>
    </div>
  );
};

type LeftSidebarProps = {
  collapsed?: boolean;
  onExpand?: () => void;
  onCollapse?: () => void;
};

export const LeftSidebar = ({ collapsed, onExpand, onCollapse }: LeftSidebarProps) => {
  const addSection = useResumeStore((state) => state.addSection);
  const setValue = useResumeStore((state) => state.setValue);
  const layout = useResumeStore((state) => state.resume.data.metadata.layout);
  const customSections = useResumeStore((state) => state.resume.data.sections.custom);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const { sectionOrder, sectionToColumn } = useMemo(() => {
    const order: string[] = [];
    const colMap: Record<string, number> = {};

    for (const page of layout) {
      for (let colIdx = 0; colIdx < page.length; colIdx++) {
        for (const sectionId of page[colIdx]) {
          if (!order.includes(sectionId)) {
            order.push(sectionId);
            colMap[sectionId] = colIdx;
          }
        }
      }
    }

    return { sectionOrder: order, sectionToColumn: colMap };
  }, [layout]);

  const onSectionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIdx = sectionOrder.indexOf(active.id as string);
    const newIdx = sectionOrder.indexOf(over.id as string);
    if (oldIdx === -1 || newIdx === -1) return;

    const newOrder = arrayMove(sectionOrder, oldIdx, newIdx);

    const numCols = layout[0]?.length ?? 1;
    const newColumns: string[][] = Array.from({ length: numCols }, () => []);

    for (const sectionId of newOrder) {
      const col = sectionToColumn[sectionId] ?? 0;
      newColumns[col].push(sectionId);
    }

    setValue("metadata.layout", [newColumns]);
  };

  const toggleColumn = (sectionId: string) => {
    const numCols = layout[0]?.length ?? 1;
    if (numCols < 2) return;

    const currentCol = sectionToColumn[sectionId] ?? 0;
    const newCol = currentCol === 0 ? 1 : 0;

    const layoutCopy: string[][][] = JSON.parse(JSON.stringify(layout));

    for (const page of layoutCopy) {
      for (let c = 0; c < page.length; c++) {
        page[c] = page[c].filter((id) => id !== sectionId);
      }
    }

    layoutCopy[0][newCol].push(sectionId);
    setValue("metadata.layout", layoutCopy);
  };

  const numCols = layout[0]?.length ?? 1;

  const renderSection = (sectionId: string) => {
    if (sectionId === "summary") {
      return <SummarySection />;
    }

    if (sectionId.startsWith("custom.")) {
      return (
        <SectionBase<CustomSection>
          id={sectionId as SectionKey}
          title={(item) => item.name}
          description={(item) => item.description}
        />
      );
    }

    const config = configMap.get(sectionId as SectionKey);
    if (!config) return null;

    return <SectionBase id={config.id} title={config.title} description={config.description} />;
  };

  const columnLabel = (sectionId: string) => {
    const col = sectionToColumn[sectionId] ?? 0;
    return col === 0 ? t`Main` : t`Side`;
  };

  if (collapsed) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center border-r bg-secondary-accent/30 py-4">
        <Tooltip content={t`Expand sidebar`} side="right">
          <Button size="icon" variant="ghost" className="h-9 w-9" onClick={onExpand}>
            <CaretRight size={18} weight="bold" />
          </Button>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className="flex h-full min-w-0 w-full flex-col overflow-hidden" data-testid="builder-left-sidebar">
      <ScrollArea
        orientation="vertical"
        className="min-h-0 flex-1 bg-secondary-accent/30"
        fadeEdges
        scrollHint
      >
        <div className="grid gap-y-3 p-3 @container/left">
          <BasicsSection />
          <Separator />
          <FirstRunPrompt />
          <OnboardingHints />

          <DndContext
            sensors={sensors}
            onDragEnd={onSectionDragEnd}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
          >
            <SortableContext items={sectionOrder} strategy={verticalListSortingStrategy}>
              {sectionOrder.map((sectionId, index) => (
                <Fragment key={sectionId}>
                  {index > 0 && <Separator />}
                  <SortableSection
                    sectionId={sectionId}
                    columnLabel={columnLabel(sectionId)}
                    onToggleColumn={() => toggleColumn(sectionId)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1">{renderSection(sectionId)}</div>
                    </div>
                  </SortableSection>
                </Fragment>
              ))}
            </SortableContext>
          </DndContext>

          {Object.values(customSections).map((section) => {
            const customId = `custom.${section.id}`;
            if (sectionOrder.includes(customId)) return null;
            return (
              <Fragment key={section.id}>
                <Separator />
                <SectionBase<CustomSection>
                  id={customId as SectionKey}
                  title={(item) => item.name}
                  description={(item) => item.description}
                />
              </Fragment>
            );
          })}

          <Separator />

          <Button size="lg" variant="outline" onClick={addSection}>
            <PlusCircle />
            <span className="ml-2">{t`Add a new section`}</span>
          </Button>
        </div>
      </ScrollArea>
    </div>
  );
};
